import { useTranslation } from 'react-i18next';
import type { ProductStockListings } from 'types/capi/pdp';

import Tracked from '~/components/Tracked';
import WtbToggleViewEvent from '~/events/WtbToggleViewEvent';
import useRootData from '~/hooks/useRootData';
import type { TrackedButtonClickName } from '~/services/gtm.client';

import { SelectedView } from './index';

type Props = {
  selectedView: SelectedView;
  setSelectedView: (view: SelectedView) => void;
  stockListings: ProductStockListings;
};

export default function ViewToggle({
  selectedView,
  setSelectedView,
  stockListings
}: Props) {
  const { t } = useTranslation('pdp');

  const { RAFFLES, RETAILERS, MINIMISED } = SelectedView;

  const numOfRaffles = stockListings.raffles.length;
  const numOfRetailers = stockListings.affiliates.length;

  if (numOfRetailers === 0 && numOfRaffles === 0) return null;

  return (
    <div className="flex w-full justify-center gap-2">
      {numOfRetailers > 0 && (
        <Toggle
          isSelected={selectedView === RETAILERS}
          onClick={() =>
            setSelectedView(selectedView === RETAILERS ? MINIMISED : RETAILERS)
          }
          name="SHOW_ALL_RETAILERS"
        >
          {t('showRetailersButton', { numOfRetailers })}
        </Toggle>
      )}
      {numOfRaffles > 0 && (
        <Toggle
          isSelected={selectedView === RAFFLES}
          onClick={() =>
            setSelectedView(selectedView === RAFFLES ? MINIMISED : RAFFLES)
          }
          name="SHOW_ALL_RAFFLES"
        >
          Show all raffles
        </Toggle>
      )}
    </div>
  );
}

function Toggle({
  onClick,
  children,
  isSelected,
  name
}: {
  children: React.ReactNode;
  isSelected: boolean;
  name: TrackedButtonClickName;
  onClick: () => void;
}) {
  const { isBot } = useRootData();

  return (
    <Tracked.Button
      onClick={() => {
        onClick();

        return new WtbToggleViewEvent({
          isBot,
          trackedButtonName: name
        }).send();
      }}
      className="max-w-40 text-nowrap !rounded-full"
      pagePosition="WTB"
      name={name}
      variant={isSelected ? 'black' : 'white-grey-border'}
    >
      <p className="flex items-center justify-center gap-1 p-0.5 text-sm font-light">
        {children}
      </p>
    </Tracked.Button>
  );
}
